/**
 * What the mission changed beyond the ranger (04 §2.3-2.4): Enemy Readiness with its reasons,
 * rangers freed from captivity, a fallen ranger's tags brought home, and the operation's end if
 * this mission decided it.
 */

import type { Aftermath } from '../../../meta/types';
import type { AftermathInput } from '../context';
import { box, el, signed } from '../dom';
import { dossierCard } from '../widgets/dossierCard';
import type { StampSpec } from '../widgets/stamp';
import { stamp } from '../widgets/stamp';
import type { Step } from './shell';
import { continueButton, stepShell } from './shell';

/** The operation is over (04 §2.4); null while it goes on, and outside Operation mode. Pure. */
export function operationVerdict(input: AftermathInput): StampSpec | null {
  if (input.deployment.mode !== 'operation' || input.aftermath === null) return null;
  switch (input.aftermath.operationStatus) {
    case 'won':
      return { text: 'OPERATION WON', tone: 'green' };
    case 'lost':
      return { text: 'OPERATION LOST', tone: 'red' };
    case 'withdrawn':
      return { text: 'OPERATION WITHDRAWN', tone: 'amber' };
    case 'active':
      return null;
  }
}

/** Whether there is anything for this step to say. Pure. */
export function hasSummary(input: AftermathInput): boolean {
  const a = input.aftermath;
  if (a === null) return false;
  const readiness = input.deployment.mode === 'operation' && (a.readinessChange !== 0 || a.readinessReasons.length > 0);
  return readiness || a.freedRangers.length > 0 || a.tagRecovery !== undefined || operationVerdict(input) !== null;
}

function readinessBlock(a: Aftermath): HTMLElement {
  // Readiness rising is bad news for the company, so it is the red direction.
  const tone = a.readinessChange > 0 ? 'menu-bad' : a.readinessChange < 0 ? 'menu-good' : 'menu-dim';
  const head = box(
    'menu-readiness-head',
    el('span', 'menu-readiness-label', 'ENEMY READINESS'),
    el('span', `menu-readiness-change ${tone}`, signed(a.readinessChange)),
  );
  const reasons = el('div', 'menu-readiness-reasons');
  for (const reason of a.readinessReasons) reasons.append(el('div', 'menu-prose', `- ${reason}`));
  return box('menu-readiness', head, reasons);
}

export const summaryStep: Step = (env, next) => {
  const a = env.input.aftermath;
  const shell = stepShell('SITUATION REPORT');
  if (a !== null) {
    if (env.input.deployment.mode === 'operation') shell.content.append(readinessBlock(a));
    if (a.freedRangers.length > 0) {
      const grid = el('div', 'menu-freed-grid');
      for (const r of a.freedRangers) grid.append(dossierCard(r, { describe: env.ctx.describe, compact: true }));
      shell.content.append(el('div', 'menu-freed-title menu-good', 'FREED - BACK WITH THE COMPANY'), grid);
    }
    if (a.tagRecovery !== undefined) {
      const perk = a.tagRecovery.perk;
      const lesson = perk === null
        ? 'HE HAD NO PERK TO PASS ON.'
        : `${env.ctx.describe(perk).name} CAN NOW BE TAUGHT TO ONE RANGER: THEATRE MAP, HQ, TRAIN A PERK.`;
      shell.content.append(
        el('div', 'menu-freed-title menu-good', `TAGS RECOVERED - ${a.tagRecovery.name.toUpperCase()}`),
        el('div', 'menu-prose', `HIS MEDALS ARE CONFIRMED ON THE MEMORIAL WALL. ${lesson}`),
      );
    }
    const verdict = operationVerdict(env.input);
    if (verdict !== null) shell.content.append(box('menu-after-stampbox', stamp(verdict)));
  }
  const go = continueButton(next);
  shell.actions.append(go);
  return { el: shell.el, focus: go };
};
